import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoginResponse, User } from '../models';
import { StrutturaUser } from '../models/userinfo.model';
import {
  GetAdminUser,
  GetCredentials,
  GetPermessiList,
  GetSelectedStruttura,
  GetStruttureList,
  GetUserInfo,
  RemoveAuthData,
  RemoveAuthTokenData,
  RemoveSelectedStrutturaData,
  SetAdminUser,
  SetCredentials,
  SetExpires,
  SetFirebaseToken,
  SetNotificationsNumber,
  SetPermessiList,
  SetRefreshToken,
  SetStruttura,
  SetStruttureList,
  SetToken,
  SetUserID,
  SetUserInfo,
} from './storage-data.utility';
import { JWT } from './token.utility';

@Injectable({
  providedIn: 'root',
})
export class UserProvider {
  /**
   * current login credentials/tokens.
   */
  private _credentials$ = new BehaviorSubject<LoginResponse | null>(null);

  /**
   * whether the current user is an admin.
   */
  private _isAdmin$ = new BehaviorSubject<boolean | null>(null);

  /**
   * current user info.
   */
  private _currentUser$ = new BehaviorSubject<User | null>(null);

  /**
   * currently selected struttura (real home struttura or the admin pseudo-struttura).
   */
  private _selectedStruttura$ = new BehaviorSubject<StrutturaUser | null>(null);

  /**
   * permessi list for the current struttura.
   */
  private _permessiList$ = new BehaviorSubject<string[] | null>(null);

  /**
   * strutture list for the current user.
   */
  private _struttureList$ = new BehaviorSubject<StrutturaUser[] | null>(null);

  /**
   * unread notifications count.
   */
  private _notificationsNumber$ = new BehaviorSubject<number | null>(null);

  /**
   * firebase messaging token.
   */
  private _firebaseToken$ = new BehaviorSubject<string | null>(null);

  /**
   * rehydrates every observable from storage, so consumers see the persisted state right after a page reload.
   */
  constructor() {
    const savedCredentials = GetCredentials();
    const storedIsAdmin = GetAdminUser();
    const storedUserInfo = GetUserInfo();
    const storedSelectedStruttura = GetSelectedStruttura();
    const storedStruttureList = GetStruttureList();
    const storedPermessiList = GetPermessiList();

    if (savedCredentials) {
      this._credentials$.next(savedCredentials);
      this._isAdmin$.next(storedIsAdmin);
    }

    if (storedUserInfo) {
      this._currentUser$.next(storedUserInfo);
    }

    if (storedSelectedStruttura) {
      this._selectedStruttura$.next(storedSelectedStruttura);
    }

    if (storedStruttureList) {
      this._struttureList$.next(storedStruttureList);
    }

    if (storedPermessiList) {
      this._permessiList$.next(storedPermessiList);
    }
  }

  /**
   * persists the login credentials/tokens and their decoded expiry, to storage and to the provider.
   */
  setCredentials(credential: LoginResponse): void {
    const jwt = new JWT(credential.token);
    const tokenExpirationDate = jwt.getTokenExpiration();

    SetCredentials(credential);
    SetUserID(credential.user_id);
    SetToken(credential.token);
    SetExpires(tokenExpirationDate);
    SetRefreshToken(credential.refreshToken);

    this._credentials$.next(credential);
  }

  /**
   * persists the current user, to storage and to the provider.
   */
  setCurrentUser(user: User): void {
    this._currentUser$.next(user);

    SetUserInfo(user);
  }

  /**
   * persists the selected struttura, to storage and to the provider.
   */
  setSelectedStruttura(struttura: StrutturaUser): void {
    SetStruttura(struttura);

    this._selectedStruttura$.next(struttura);
  }

  /**
   * persists the admin flag, to storage and to the provider.
   */
  setIsAdmin(isAdmin: boolean): void {
    this._isAdmin$.next(isAdmin);

    SetAdminUser(isAdmin);
  }

  /**
   * persists the strutture list, to storage and to the provider.
   */
  setStruttureList(strutture: StrutturaUser[]): void {
    this._struttureList$.next(strutture);

    SetStruttureList(strutture);
  }

  /**
   * persists the permessi list, to storage and to the provider.
   */
  setPermissionList(permessiVisivi: string[]): void {
    this._permessiList$.next(permessiVisivi);

    SetPermessiList(permessiVisivi);
  }

  /**
   * persists the notifications count, to storage and to the provider.
   */
  setNotificationsNumber(num: number): void {
    this._notificationsNumber$.next(num);

    SetNotificationsNumber(num);
  }

  /**
   * persists the firebase messaging token, to storage and to the provider.
   */
  setFirebaseToken(token: string): void {
    this._firebaseToken$.next(token);

    SetFirebaseToken(token);
  }

  /**
   * checks whether the user is authenticated.
   * @returns {boolean} true if login credentials are present.
   */
  isAuthenticated(): boolean {
    return !!this._credentials$.getValue();
  }

  /**
   * streams the login credentials.
   * @returns {Observable<LoginResponse>} an observable of the current credentials.
   */
  get credentials$(): Observable<LoginResponse> {
    return this._credentials$.asObservable();
  }

  /**
   * streams the current user.
   * @returns {Observable<User | null>} an observable of the current user.
   */
  get currentUser$(): Observable<User | null> {
    return this._currentUser$.asObservable();
  }

  /**
   * reads the current user from storage.
   * @returns {User} the current user, or null if none is stored.
   */
  get userInfo(): User {
    const storedUsed = GetUserInfo() as User;

    return storedUsed || null;
  }

  /**
   * reads the admin flag from storage.
   * @returns {boolean} true if the user is an admin.
   */
  get isAdminUser(): boolean {
    return GetAdminUser();
  }

  /**
   * checks whether the user is an admin with no permission restrictions.
   * @returns {boolean} true if the user is a super admin.
   */
  get isSuperAdmin(): boolean {
    const user = this.userInfo;
    const isAdmin = this.isAdminUser;
    const adminPermissions = user?.strutturaAdmin?.permessiVisivi || [];

    return isAdmin && adminPermissions.length === 0;
  }

  /**
   * checks whether the user holds a specific admin permission.
   * @returns {boolean} true if the user has the permission.
   */
  hasAdminPermission(permission: string): boolean {
    const user = this.userInfo;
    const adminPermissions = user?.strutturaAdmin?.permessiVisivi || [];

    return adminPermissions.includes(permission);
  }

  /**
   * checks whether the user holds a specific feature permission.
   * @returns {boolean} true if the user has the permission.
   */
  hasFeaturePermission(permission: string): boolean {
    const permessiVisivi = GetPermessiList();
    return permessiVisivi.includes(permission);
  }

  /**
   * reads the selected struttura from storage.
   * @returns {StrutturaUser | null} the selected struttura, or null if none is stored.
   */
  get currentStruttura(): StrutturaUser | null {
    const storedStruttura = GetSelectedStruttura() as StrutturaUser;

    return storedStruttura || null;
  }

  /**
   * streams the selected struttura.
   * @returns {Observable<StrutturaUser | null>} an observable of the selected struttura.
   */
  get selectedStruttura$(): Observable<StrutturaUser | null> {
    return this._selectedStruttura$.asObservable();
  }

  /**
   * streams the unread notifications count.
   * @returns {Observable<number | null>} an observable of the notifications count.
   */
  get notifications$(): Observable<number | null> {
    return this._notificationsNumber$.asObservable();
  }

  /**
   * reads the strutture list from storage.
   * @returns {StrutturaUser[]} the strutture list, or an empty array if none is stored.
   */
  get userStruttureList(): StrutturaUser[] {
    const storedStrutture = GetStruttureList();

    return storedStrutture || [];
  }

  /**
   * streams the permessi list.
   * @returns {Observable<string[]>} an observable of the permessi list.
   */
  get permissionList$(): Observable<string[]> {
    return this._permessiList$.asObservable();
  }

  /**
   * reads the current in-memory permessi list.
   * @returns {string[] | null} the permessi list, or null if none was set.
   */
  get currentPermissionList(): string[] | null {
    return this._permessiList$.getValue();
  }

  /**
   * clears the selected struttura and permessi, in memory and in storage, e.g. when a guard finds them stale.
   */
  clearSelectedStruttura(): void {
    this._selectedStruttura$.next(null);
    this._permessiList$.next(null);

    RemoveSelectedStrutturaData();
  }

  /**
   * clears every in-memory observable; also wipes storage when called on a real logout.
   */
  clearUserProviderForLogout(areLogout?: boolean): void {
    this._credentials$.next(null);
    this._currentUser$.next(null);
    this._permessiList$.next(null);
    this._struttureList$.next(null);
    this._selectedStruttura$.next(null);
    this._notificationsNumber$.next(null);
    if (areLogout) {
      RemoveAuthData();
    } else {
      RemoveAuthTokenData();
    }
  }
}
